import React, { Fragment, useCallback, useEffect, useState } from "react";
import "./index.less";
import { Link, useParams } from "react-router-dom";
import { isBrowser, isMobile } from "react-device-detect";
import {
  Spin,
  Row,
  Col,
  Typography,
  Select,
  Button,
  message,
  Breadcrumb,
  Alert,
} from "antd";
import { LeftOutlined, ShoppingOutlined } from "@ant-design/icons";
import ReactImageGallery from "react-image-gallery";
import { DocumentTitle, NetworkError } from "../../components";
import NotFound from "../NotFound";
import I18n from "../../i18n";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import { loadProduct } from "../../../application/product-slice";
import { addToCart } from "../../../application/cart-slice";
import { Color } from "../../../domain";

const { Option } = Select;

type ProductRouteParams = { id: string; title: string | undefined };

export default function Product() {
  const { id, title } = useParams<ProductRouteParams>();
  const dispatch = useAppDispatch();
  const { loading, product, failure } = useAppSelector(
    (state) => state.product
  );

  const [selectedColorId, setSelectedColorId] = useState<number>();
  const [selectedSize, setSelectedSize] = useState<string>();
  const [activeColors, setActiveColors] = useState<Color[]>([]);

  const selectedAttribute = product?.attributes.find(
    (attribute) =>
      attribute.color.id === selectedColorId && attribute.size === selectedSize
  );

  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    stableDispatch(loadProduct(id));
  }, [stableDispatch, id]);

  useEffect(() => {
    if (loading === "succeeded" && product !== undefined) {
      setSelectedSize(product.getFirstAvailableAttribute().size);
    } else {
      setSelectedSize(undefined);
    }
  }, [loading, product]);

  useEffect(() => {
    if (product !== undefined && selectedSize !== undefined) {
      const activeColors = product.attributes
        .filter((attribute) => attribute.size === selectedSize)
        .map((attribute) => attribute.color)
        .filter(
          (color, index, array) =>
            array.findIndex((c) => c.id === color.id) === index
        );
      setActiveColors(activeColors);
      let isColorActive = false;
      if (selectedColorId !== undefined) {
        isColorActive =
          activeColors.find((e) => e.id === selectedColorId) === undefined
            ? false
            : true;
      }
      if (isColorActive === false) setSelectedColorId(activeColors[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, selectedSize]);

  if (loading === "failed") {
    const productNotFound = failure?.maybeMap({
      productNotFoundFailure: () => true,
      orElse: () => false,
    });
    if (productNotFound) return <NotFound />;
  }

  const addToCartSubmitted = () => {
    if (product !== undefined && selectedAttribute !== undefined) {
      dispatch(
        addToCart({
          attributeId: selectedAttribute.id,
          product: product,
          productId: id,
          count: 1,
        })
      );
      message.success(I18n.general.addedToCartSuccessfully);
    }
  };

  const getCaytegoryLink = (categoryId?: string, categoryTitle?: string) => {
    return `/category/${categoryId}/${
      categoryTitle ? encodeURIComponent(categoryTitle) : ""
    }`;
  };

  const isLoading = loading === "idle" || loading === "pending";
  const reactGalleryItems = product?.images.map((image) => {
    return {
      original: image,
      originalTitle: product.title,
      thumbnail: image,
    };
  });

  if (loading === "failed") return <NetworkError />;
  const offPercent = selectedAttribute
    ? Math.floor(
        ((selectedAttribute.price - selectedAttribute.offPrice) /
          selectedAttribute.price) *
          100
      )
    : undefined;
  return (
    <DocumentTitle title={title ? decodeURIComponent(title) : undefined}>
      <Spin spinning={isLoading}>
        <Breadcrumb
          separator={<LeftOutlined />}
          style={isBrowser ? { padding: "16px 175px" } : { padding: 16 }}
        >
          <Breadcrumb.Item>
            <Link to="/">فروشگاه بهارا برند</Link>
          </Breadcrumb.Item>
          {product?.generalCategory !== undefined ? (
            <Fragment>
              <Breadcrumb.Item>
                <Link
                  to={getCaytegoryLink(
                    product?.generalCategory.id,
                    product?.generalCategory.title
                  )}
                >
                  {product?.generalCategory.title}
                </Link>
              </Breadcrumb.Item>
              {product?.subCategory !== undefined ? (
                <Fragment>
                  <Breadcrumb.Item>
                    <Link
                      to={getCaytegoryLink(
                        `${product?.generalCategory.id}-${product?.subCategory.id}`,
                        product?.subCategory.title
                      )}
                    >
                      {product?.subCategory.title}
                    </Link>
                  </Breadcrumb.Item>
                  {product?.category !== undefined ? (
                    <Breadcrumb.Item>
                      <Link
                        to={getCaytegoryLink(
                          `${product?.generalCategory.id}-${product?.subCategory.id}-${product?.category.id}`,
                          product?.category.title
                        )}
                      >
                        {product?.category.title}
                      </Link>
                    </Breadcrumb.Item>
                  ) : (
                    <Fragment />
                  )}
                </Fragment>
              ) : (
                <Fragment />
              )}
            </Fragment>
          ) : (
            <Fragment />
          )}
        </Breadcrumb>
        <Row
          style={
            isBrowser
              ? { padding: "0 165px", marginBottom: 32 }
              : { marginBottom: 32 }
          }
        >
          <Col lg={12} md={12}>
            <ReactImageGallery
              items={reactGalleryItems === undefined ? [] : reactGalleryItems}
              infinite
              showNav={false}
              showThumbnails
              thumbnailPosition={isMobile ? "bottom" : "left"}
              showFullscreenButton={false}
              useBrowserFullscreen={false}
              showPlayButton={false}
              isRTL
              showIndex
            />
          </Col>
          <Col lg={12} md={12} style={{ padding: "0 16px" }}>
            <h1
              className={
                isMobile
                  ? "product-title product-title-mobile"
                  : "product-title"
              }
            >
              {product?.title}
            </h1>
            <div className="product-description">
              <Typography.Text>{product?.description}</Typography.Text>
            </div>
            {selectedAttribute && selectedAttribute.offPrice !== 0 ? (
              <Fragment>
                <Row align="bottom" style={{ margin: "10px 0 0 0" }}>
                  <span className="product-off-price">
                    {selectedAttribute?.price.toLocaleString("fa")} تومان
                  </span>
                </Row>
                <Row align="bottom" style={{ margin: "0 0 10px 0" }}>
                  <span className="product-price-value">
                    {selectedAttribute?.offPrice.toLocaleString("fa")}
                  </span>
                  <span className="product-price-currency">تومان</span>
                  <div className="product-off-badge">{`%${offPercent} تخفیف`}</div>
                  {product?.hasLabel() ? (
                    <div
                      style={{
                        backgroundColor: "#F5F5F5",
                        padding: 8,
                        marginRight: 8,
                        borderRadius: 4,
                        fontSize: 12,
                      }}
                    >
                      {product?.label}
                    </div>
                  ) : (
                    <Fragment />
                  )}
                </Row>
              </Fragment>
            ) : (
              <Row align="bottom" style={{ margin: "10px 0" }}>
                <span className="product-price-value">
                  {selectedAttribute?.price.toLocaleString("fa")}
                </span>
                <span className="product-price-currency">تومان</span>
                {product?.hasLabel() ? (
                  <div
                    style={{
                      backgroundColor: "#F5F5F5",
                      padding: 8,
                      marginRight: 8,
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                  >
                    {product?.label}
                  </div>
                ) : (
                  <Fragment />
                )}
              </Row>
            )}
            <Row className="color-and-size-section">
              <Col xs={12}>
                <div className="select-color">
                  <div className="filters-title">{I18n.general.chooseSize}</div>
                  <Select
                    placeholder={I18n.general.chooseSize}
                    style={{ width: "100%" }}
                    onSelect={(value, _) => setSelectedSize(value as string)}
                    value={selectedSize}
                  >
                    {product?.sizes?.map((size, index) => (
                      <Option key={index} value={size}>
                        {size}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
              <Col xs={12}>
                <div className="select-size">
                  <div className="filters-title">
                    {I18n.general.chooseColor}
                  </div>
                  <Select
                    style={{ width: "100%" }}
                    onSelect={(value, _) => setSelectedColorId(value)}
                    value={selectedColorId}
                  >
                    {activeColors.map((color, index) => (
                      <Option key={index} value={color.id}>
                        <Row align="middle">
                          <span
                            className="select-color-option"
                            style={{ backgroundColor: color.color }}
                          >
                            {" "}
                          </span>
                          <Typography.Text>{color.title}</Typography.Text>
                        </Row>
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
            </Row>
            {selectedAttribute?.count === 0 ? (
              <Alert
                type="error"
                message="این محصول با این رنگ و سایز در حال حاضر موجود نمی‌باشد."
              />
            ) : (
              <Row>
                <Col
                  xs={isMobile ? 24 : 12}
                  style={isMobile ? {} : { paddingLeft: 8 }}
                >
                  <Button
                    type="primary"
                    icon={<ShoppingOutlined />}
                    onClick={addToCartSubmitted}
                    block
                  >
                    {I18n.general.addToCart}
                  </Button>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Spin>
    </DocumentTitle>
  );
}
